import { getCurrencySymbol } from '@angular/common';
import { AppConfigService } from '@core/app-config';
import { DealsService } from '@core/deals';
import { PersonalizedContentService } from '@core/personalized-content/personalized-content.service';
import { OfferBlockDataType } from '@feature/types/offer-block.type';
import {
  PromoBlockFlagSettings,
  PromoBlockImageSetup,
  PromoBlockView,
  ViewPromoBlock,
} from '@feature/types/promo-block.type';
import { FlightDeal } from '@shared/types';
import { DealLegalTextFormatterPipe } from '@shared/ui/pipes/dealLegalFormatterPipe/deal-legal-text-formatter.pipe';
import { filterDealsByRestrictDestinations } from '@shared/ui/utils/deal-utils/filter-deals-by-restrict-destinations';
import { getDefaultAllowedOrigin } from '@shared/ui/utils/deal-utils/get-default-allowed-origin';
import { booleanStringCheck } from '@shared/ui/utils/global-utils/boolean-string-check';
import { BookerFacade } from '@store/booker/booker.facade';
import { OriginsFacade } from '@store/origins/origins.facade';
import { JbThemeEnum } from 'jb-component-library';
import { Observable, of } from 'rxjs';
import {
  filter,
  map,
  pluck,
  share,
  startWith,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';

import { tryTakeDynamicExpression } from '../../core/personalized-content/personalized-content.utils';
import { SupportsDynamicExpressions } from '../../core/personalized-content/types/personalized-content.type';

export class PromoBlock implements PromoBlockView {
  // component properties
  dynamicContent: Observable<any>;
  dynamicTitle: Observable<any>;
  fullBleed = false;
  dynamicImage: PromoBlockImageSetup = {
    url: '',
    isBackground: false,
    isForeground: false,
  };

  // Promo Block View properties
  fullPromoBlockLink: string;
  flagSettings: PromoBlockFlagSettings;
  offerBlocks: OfferBlockDataType[];
  offerBlockTheme: JbThemeEnum;
  promoBlock: SupportsDynamicExpressions<ViewPromoBlock>;

  // observables
  onloadSelectedOrigin$: Observable<string>;
  afterLoadFlightDeal$: Observable<any>;
  afterLoadDynamicUrl$: Observable<string>;
  dynamicCurrencyCode$: Observable<string>;
  dynamicImage$: Observable<PromoBlockImageSetup>;
  dynamicLegalCopy$: Observable<string>;

  constructor(
    private personalizedContentService: PersonalizedContentService,
    private dealsService: DealsService,
    private originsFacade: OriginsFacade,
    private bookerFacade: BookerFacade,
    private appConfigService: AppConfigService,
    private data: PromoBlockView,
    private dealLegalTextFormatterPipe: DealLegalTextFormatterPipe,
  ) {
    this.promoBlock = this.data.promoBlock;
    this.fullPromoBlockLink = this.data.fullPromoBlockLink;
    this.flagSettings = this.data.flagSettings;
    this.offerBlocks = this.data.offerBlocks;
    this.offerBlockTheme = this.data.offerBlockTheme;
    this.fullBleed = booleanStringCheck(this.promoBlock.fullBleed);

    // get dynamic content
    const promoBlockWithDynamicContent =
      this.personalizedContentService.initializeDataWithDynamicExpression(
        this.promoBlock,
      );
    this.dynamicContent = tryTakeDynamicExpression(
      promoBlockWithDynamicContent,
      'content',
    );
    this.dynamicTitle = tryTakeDynamicExpression(
      promoBlockWithDynamicContent,
      'title',
    );

    // setup observables
    this.onloadSelectedOrigin$ = this.originsFacade.nearbyAirportCodes.pipe(
      filter(nearbyAirportCodes => nearbyAirportCodes != null),
      withLatestFrom(this.bookerFacade.recentAirSearches),
      take(1),
      map(([nearbyAirportCodes, recentAirports]) => {
        const dynamicOfferDefaultOrigin =
          this.promoBlock.dynamicOffer?.[0].offerSettings.origins[0].default;
        const allowedOrigins =
          this.promoBlock.dynamicOffer?.[0].offerSettings.origins[0].restrict;

        return getDefaultAllowedOrigin(
          dynamicOfferDefaultOrigin,
          allowedOrigins,
          nearbyAirportCodes,
          recentAirports,
        );
      }),
    );

    this.afterLoadFlightDeal$ = this.onloadSelectedOrigin$.pipe(
      filter(() => !!this.promoBlock.dynamicOffer?.[0].offerSettings),
      filter(selectedOrigin => !!selectedOrigin),
      switchMap(selectedOrigin =>
        this.dealsService
          .getFlightsDeals(
            selectedOrigin,
            this.promoBlock.dynamicOffer[0].offerSettings.offerBlock[0]
              .valueType === 'points',
            100,
          )
          .pipe(
            map(results =>
              this.promoBlock.dynamicOffer[0].offerSettings.results[0].restrict
                ?.length
                ? filterDealsByRestrictDestinations(
                    results,
                    this.promoBlock.dynamicOffer[0].offerSettings.results[0]
                      ?.restrict,
                    1,
                  )
                : results,
            ),
            pluck(0),
            switchMap(data =>
              data
                ? of(data)
                : this.dealsService
                    .getFlightsDeals(
                      this.promoBlock.dynamicOffer[0].offerSettings.origins[0]
                        .default,
                      this.promoBlock.dynamicOffer[0].offerSettings
                        .offerBlock[0].valueType === 'points',
                      100,
                    )
                    .pipe(
                      map(results =>
                        this.promoBlock.dynamicOffer[0].offerSettings.results[0]
                          .restrict?.length
                          ? filterDealsByRestrictDestinations(
                              results,
                              this.promoBlock.dynamicOffer[0].offerSettings
                                .results?.[0]?.restrict,
                              1,
                            )
                          : results,
                      ),
                      pluck(0),
                    ),
            ),
            filter(data => !!data),
            map(result =>
              this.mapDynamicSalesData(
                result,
                this.promoBlock.dynamicOffer[0].offerSettings.offerBlock[0],
              ),
            ),
          ),
      ),
      take(1),
      share(),
    );

    this.afterLoadDynamicUrl$ = this.afterLoadFlightDeal$.pipe(
      pluck('url'),
      filter(val => !!val),
    );

    this.dynamicCurrencyCode$ = this.afterLoadFlightDeal$.pipe(
      pluck('currencyCode'),
      map(currencyCode => getCurrencySymbol(currencyCode || 'USD', 'narrow')),
    );

    this.afterLoadFlightDeal$
      .pipe(
        take(1),
        pluck('destinationAirportCode'),
        filter(destinationCode => !!destinationCode),
        filter(
          () =>
            this.promoBlock.dynamicOffer?.[0]?.offerSettings?.showImage?.toLowerCase() ===
            'true',
        ),
        map(selectedDestination => this.imageSetup(selectedDestination)),
        startWith({
          url: '',
          isBackground: false,
          isForeground: false,
        }),
      )
      .subscribe(data => (this.dynamicImage = data)); // don't need unsubscribe cause of take(1)

    this.dynamicLegalCopy$ = this.afterLoadFlightDeal$.pipe(
      filter(
        () => !!this.promoBlock.dynamicOffer[0].offerSettings.legalCopy[0],
      ),
      map(data =>
        this.dealLegalTextFormatterPipe.transform(
          data,
          this.promoBlock.dynamicOffer[0].offerSettings.legalCopy[0].content,
        ),
      ),
    );
  }

  mapDynamicSalesData(
    result: FlightDeal,
    dynamicOfferBlock: OfferBlockDataType,
  ) {
    return {
      ...result,
      ...dynamicOfferBlock,
      url:
        dynamicOfferBlock.valueType === 'points'
          ? `${result.url}&fare=POINTS`
          : result.url,
    };
  }

  imageSetup(destinationAirportCode: string): PromoBlockImageSetup {
    return {
      isForeground:
        (this.promoBlock.dynamicOffer[0]?.useImageAs ||
          this.promoBlock.dynamicOffer[0]?.overlayImage?.toLowerCase()) ===
        'foreground',
      isBackground:
        (this.promoBlock.dynamicOffer[0]?.useImageAs ||
          this.promoBlock.dynamicOffer[0]?.overlayImage?.toLowerCase()) ===
        'background',
      url: `${this.appConfigService.magnoliaUrl}/dam${this.appConfigService.paths[0].flightBlockImagePrefix}/${destinationAirportCode}.jpg`,
    };
  }
}
